import React, { useEffect, useRef } from "react";
import {
  Box,
  VStack,
  Flex,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react";
import { ChatMessage as ChatMessageType } from "views/admin/chatbot/chatbot";
import ChatMessage from "views/admin/chatbot/components/ChatMessage";
import StreamingMessage from "views/admin/chatbot/components/StreamingMessage";

interface ChatContainerProps {
  messages: ChatMessageType[];
  currentStreamingMessage?: string;
  isStreaming?: boolean;
  error?: string | null;
  isLoading?: boolean;
}

const ChatContainer: React.FC<ChatContainerProps> = ({
  messages,
  currentStreamingMessage = "",
  isStreaming = false,
  error = null,
  isLoading = false,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentStreamingMessage]);

  const hasContent = messages.length > 0 || isStreaming || isLoading;

  return (
    <Box
      flex={1}
      bg={bgColor}
      borderLeft="1px solid"
      borderRight="1px solid"
      borderColor={borderColor}
      position="relative"
    >
      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Box
        height="100%"
        overflowY="auto"
        p={4}
        css={{
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#cbd5e0",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#a0aec0",
          },
        }}
      >
        {!hasContent && (
          <Flex
            height="100%"
            align="center"
            justify="center"
            direction="column"
            opacity={0.6}
          >
            <Text fontSize="lg" fontWeight="medium" mb={2}>
              🤖 Max AI
            </Text>
            <Text fontSize="sm" textAlign="center" maxW="md">
              Hello! I'm your AI assistant. I can help you with information
              about customers, partners, and answer questions about Gamelight.
              How can I assist you today?
            </Text>
          </Flex>
        )}

        <VStack spacing={4} align="stretch">
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}

          {isStreaming && (
            <StreamingMessage
              message={currentStreamingMessage}
              isTyping={!currentStreamingMessage}
            />
          )}
        </VStack>

        <div ref={messagesEndRef} />
      </Box>
    </Box>
  );
};

export default ChatContainer;
